import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import useSeo from "../../hooks/useSeo";
import "./Public.css";

const services = [
  {
    title: "Drywall",
    eyebrow: "Walls & repairs",
    body:
      "Drywall work sets the foundation for the finished room. We handle repairs, patching, surface correction, and drywall finishing with attention to clean seams, smooth transitions, and proper preparation before paint.",
    points: ["Drywall patching and repairs", "Board replacement where needed", "Taping, mudding, sanding, and surface prep"],
  },
  {
    title: "Painting",
    eyebrow: "Prep & finish coats",
    body:
      "Interior painting depends on preparation. Walls, ceilings, trim, and repaired surfaces need to be cleaned, patched, protected, and coated properly so the finished space looks consistent.",
    points: ["Wall and ceiling painting", "Trim and door painting", "Surface prep and touch-up work"],
  },
  {
    title: "Flooring",
    eyebrow: "Surfaces underfoot",
    body:
      "Flooring changes the way a room feels and functions. We focus on clean installation, practical transitions, and a finished surface that suits the space and daily use.",
    points: ["Flooring replacement", "Room-by-room installation", "Transitions and finishing details"],
  },
  {
    title: "Moulding",
    eyebrow: "Trim details",
    body:
      "Moulding and trim complete the room. Baseboards, casing, and small finish details help the work feel intentional instead of unfinished.",
    points: ["Baseboards and casing", "Trim repair and replacement", "Caulking, filling, and finish preparation"],
  },
];

export default function InteriorRenovations() {
  useSeo({
    title: "Interior Renovations | Crowned Roofing",
    description:
      "Interior renovation and finishing services including drywall, painting, flooring, and moulding work.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: "https://www.crownedroofing.com/interior-renovations",
  });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Interior Renovations | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Interior renovation and finishing services including drywall, painting, flooring, and moulding work."
      );
    }
  }, []);

  return (
    <main className="publicPage interiorPage">
      <Header />

      <section className="interiorHero">
        <div className="interiorHeroInner">
          <div className="interiorHeroCopy">
            <span className="fenceEyebrow">Interior finishing</span>
            <h1>Interior Renovations That Finish the Space Properly</h1>
            <p>
              Drywall, painting, flooring, and moulding work planned around clean preparation,
              practical sequencing, and a finished result that feels complete.
            </p>
            <button className="rBtn" onClick={() => navigate("/contact")}>
              REQUEST AN INTERIOR ESTIMATE
            </button>
          </div>
          <div className="interiorHeroList" aria-label="Interior renovation services">
            {services.map((service) => (
              <div className="interiorHeroItem" key={service.title}>
                {service.title}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="interiorIntro">
        <div className="container interiorIntroGrid">
          <h2>
            <span className="rAccent" aria-hidden="true" />
            One room or several connected finishes
          </h2>
          <div className="interiorIntroText">
            <p>
              Interior work is often connected. A drywall repair affects painting. New flooring
              changes trim details. Moulding needs clean prep before paint. Treating these pieces
              separately can leave the room looking patched together.
            </p>
            <p>
              We keep the scope organized so the work moves in the right order and the finished
              space feels intentional from wall surface to floor line.
            </p>
          </div>
        </div>
      </section>

      <section className="interiorServices">
        <div className="container">
          <div className="fenceSectionHead">
            <h2>Interior Services</h2>
            <p>Each service area gets the attention it needs before the final finish goes on.</p>
          </div>

          <div className="interiorServiceStack">
            {services.map((service, index) => (
              <article className="interiorServiceBlock" key={service.title}>
                <div className="interiorServiceNumber">{String(index + 1).padStart(2, "0")}</div>
                <div className="interiorServiceContent">
                  <span>{service.eyebrow}</span>
                  <h3>{service.title}</h3>
                  <p>{service.body}</p>
                </div>
                <ul className="interiorServiceList">
                  {service.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="interiorCta">
        <div className="interiorCtaInner">
          <h2>Ready to finish the interior?</h2>
          <p>
            Tell us what you want handled, and we can review the space, organize the scope,
            and define the next step.
          </p>
          <button className="rBtn" onClick={() => navigate("/contact")}>
            REQUEST AN INTERIOR ESTIMATE
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
